/**
 * 🚨 CRITICAL: NO HOVER EFFECTS ON BOOK READING PAGES 🚨
 *
 * The "default" variant is used for book reading pages.
 * Hover effects on reading pages are EXTREMELY distracting and have been
 * repeatedly removed by user request.
 *
 * DO NOT add hover:* classes to the "default" variant.
 * DO NOT add hover:* classes to any buttons/elements used in book readers.
 *
 * If you need hover effects, use the "elevated" or "interactive" variants.
 * Book reading should be a calm, distraction-free experience.
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UnifiedButton } from '@/components/ui/UnifiedButton';

const unifiedCardVariants = cva(
  'relative overflow-hidden rounded-[1.3rem] transition-all duration-300',
  {
    variants: {
      variant: {
        // NO HOVER EFFECTS ON DEFAULT - Used for book reading pages where hover is distracting
        default: 'bg-glass-medium backdrop-blur-md border border-glass-medium',
        elevated:
          'bg-glass-medium backdrop-blur-md border border-glass-medium ' +
          'shadow-lg hover:shadow-xl hover:border-cyber-purple/30 ' +
          'hover:scale-[1.02] hover:bg-glass-heavy',
        interactive:
          'bg-glass-medium backdrop-blur-md border border-glass-medium cursor-pointer ' +
          'hover:border-cyber-cyan/40 hover:bg-glass-heavy hover:shadow-[0_0_20px_rgba(0,212,255,0.2)] ' +
          'active:scale-[0.99] active:transition-none',
        gradient:
          'bg-glass-medium backdrop-blur-md ' +
          'before:absolute before:inset-0 before:rounded-[1.3rem] before:p-[1px] ' +
          'before:bg-glass-medium before:from-cyber-cyan before:via-cyber-purple before:to-cyber-pink ' +
          'before:-z-10 hover:before:p-[2px] hover:shadow-[0_0_30px_rgba(147,51,234,0.3)]',
        solid: 'bg-dark-800 border border-dark-700 ' + 'hover:bg-dark-700 hover:border-dark-600',
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
      orientation: {
        vertical: 'flex flex-col',
        horizontal: 'flex flex-row',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      animation: {
        none: '',
        fade: 'animate-in fade-in duration-500',
        slide: 'animate-in slide-in-from-bottom duration-500',
        scale: 'animate-in zoom-in-95 duration-500',
      },
      hoverEffect: {
        none: '',
        lift: 'hover:-translate-y-1',
        glow: 'hover:shadow-[0_0_30px_rgba(0,212,255,0.3)]',
        pulse: 'hover:animate-pulse-slow',
      },
      status: {
        none: '',
        success: 'border-green-500/50 bg-green-500/5',
        warning: 'border-yellow-500/50 bg-yellow-500/5',
        error: 'border-destructive/50 bg-destructive/5',
        info: 'border-cyber-cyan/50 bg-cyber-cyan/5',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
      orientation: 'vertical',
      fullWidth: false,
      animation: 'none',
      hoverEffect: 'none',
      status: 'none',
    },
  },
);

const cardHeaderVariants = cva('flex items-center justify-between gap-4', {
  variants: {
    padding: {
      none: '',
      sm: 'p-4 pb-0',
      md: 'p-6 pb-0',
      lg: 'p-8 pb-0',
    },
  },
  defaultVariants: {
    padding: 'md',
  },
});

const cardFooterVariants = cva('flex items-center gap-4', {
  variants: {
    padding: {
      none: '',
      sm: 'p-4 pt-0',
      md: 'p-6 pt-0',
      lg: 'p-8 pt-0',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
  },
  defaultVariants: {
    padding: 'md',
    justify: 'start',
  },
});

// Main Card Component
export interface UnifiedCardProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof unifiedCardVariants> {
  asChild?: boolean;
  loading?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
  as?: 'div' | 'article' | 'section';
  badge?: React.ReactNode;
  icon?: React.ReactNode;
  overlay?: React.ReactNode;
  progress?: number;
  showStatusIcon?: boolean;
  onClose?: () => void;
  href?: string;
  containerClassName?: string;
}

const UnifiedCard = React.forwardRef<HTMLElement, UnifiedCardProps>(
  (
    {
      className,
      containerClassName,
      variant,
      padding,
      orientation,
      fullWidth,
      animation,
      hoverEffect,
      status,
      loading = false,
      disabled = false,
      as: Component = 'div',
      badge,
      icon,
      overlay,
      progress,
      showStatusIcon = false,
      onClose,
      href,
      children,
      onClick,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const isInteractive = !!onClick || !!href || variant === 'interactive';
    const semanticTag = isInteractive ? 'article' : Component;

    const statusIcon = React.useMemo(() => {
      if (!showStatusIcon || status === 'none') return null;

      const iconClass = 'h-5 w-5';
      switch (status) {
        case 'success':
          return <CheckCircle className={cn(iconClass, 'text-green-500')} />;
        case 'warning':
          return <AlertTriangle className={cn(iconClass, 'text-yellow-500')} />;
        case 'error':
          return <AlertCircle className={cn(iconClass, 'text-destructive')} />;
        case 'info':
          return <Info className={cn(iconClass, 'text-cyber-cyan')} />;
        default:
          return null;
      }
    }, [showStatusIcon, status]);

    const cardContent = (
      <>
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-[1.3rem]">
            <Loader2 className="h-8 w-8 animate-spin text-cyber-cyan" />
          </div>
        )}

        {/* Custom Overlay */}
        {overlay && <div className="absolute inset-0 z-10 rounded-[1.3rem]">{overlay}</div>}

        {/* Progress Bar */}
        {progress !== undefined && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-glass-medium">
            <div
              className="h-full bg-glass-medium backdrop-blur-md transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}

        {/* Close Button */}
        {onClose && (
          <UnifiedButton
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            className={cn(
              'absolute top-4 right-4 z-30 p-1 rounded-full',
              'bg-glass-medium',
              'text-muted-foreground',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-cyber-purple/50',
            )}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </UnifiedButton>
        )}

        {/* Badge */}
        {badge && <div className="absolute top-4 left-4 z-20">{badge}</div>}

        {/* Status Icon */}
        {statusIcon && <div className="absolute top-4 right-4 z-20">{statusIcon}</div>}

        {/* Main Icon */}
        {icon && !loading && (
          <div className="absolute top-4 left-4 z-20 text-cyber-cyan">{icon}</div>
        )}

        {/* Card Content */}
        {icon || onClose ? (
          <div className={cn('relative z-0', icon && 'pt-12', onClose && 'pr-12')}>{children}</div>
        ) : (
          children
        )}
      </>
    );

    const cardElement = React.createElement(
      semanticTag,
      {
        ref: ref as React.Ref<HTMLElement>,
        className: cn(
          unifiedCardVariants({
            variant,
            padding: padding === 'none' ? 'none' : undefined,
            orientation,
            fullWidth,
            animation,
            hoverEffect,
            status,
          }),
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          containerClassName,
          className,
        ),
        onClick: isInteractive && !disabled ? onClick : undefined,
        role: isInteractive ? 'button' : undefined,
        tabIndex: isInteractive && !disabled ? 0 : undefined,
        'aria-disabled': disabled,
        'aria-busy': loading,
        'data-testid': dataTestId,
        ...props,
      },
      cardContent,
    );

    // Wrap in anchor tag if href is provided
    if (href && !disabled) {
      return (
        <a href={href} className="no-underline" onClick={onClick}>
          {cardElement}
        </a>
      );
    }

    return cardElement;
  },
);
UnifiedCard.displayName = 'UnifiedCard';

// Card Header Component
export interface CardHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardHeaderVariants> {
  actions?: React.ReactNode;
  'data-testid'?: string;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, padding, actions, children, 'data-testid': dataTestId, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardHeaderVariants({ padding }), className)}
      data-testid={dataTestId}
      {...props}
    >
      <div className="flex-1 space-y-1.5">{children}</div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  ),
);
CardHeader.displayName = 'CardHeader';

// Card Title Component
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  gradient?: boolean;
  'data-testid'?: string;
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  (
    {
      className,
      as: Component = 'h3',
      gradient = false,
      children,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const content = gradient ? (
      <span className="bg-glass-medium backdrop-blur-md bg-clip-text text-transparent">
        {children}
      </span>
    ) : (
      children
    );

    return React.createElement(
      Component,
      {
        ref: ref as React.Ref<HTMLHeadingElement>,
        className: cn('text-2xl font-semibold leading-none tracking-tight', className),
        'data-testid': dataTestId,
        ...props,
      },
      content,
    );
  },
);
CardTitle.displayName = 'CardTitle';

// Card Description Component
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  'data-testid'?: string;
}

const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, 'data-testid': dataTestId, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      data-testid={dataTestId}
      {...props}
    />
  ),
);
CardDescription.displayName = 'CardDescription';

// Card Content Component
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
  'data-testid'?: string;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, noPadding = false, 'data-testid': dataTestId, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(!noPadding && 'p-6 pt-4', className)}
      data-testid={dataTestId}
      {...props}
    />
  ),
);
CardContent.displayName = 'CardContent';

// Card Footer Component
export interface CardFooterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardFooterVariants> {
  'data-testid'?: string;
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, padding, justify, 'data-testid': dataTestId, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardFooterVariants({ padding, justify }), className)}
      data-testid={dataTestId}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

// Card Image Component
export interface CardImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  aspectRatio?: 'square' | 'video' | 'portrait' | 'wide';
  position?: 'top' | 'bottom' | 'left' | 'right';
  overlay?: boolean;
  'data-testid'?: string;
}

const CardImage = React.forwardRef<HTMLImageElement, CardImageProps>(
  (
    {
      className,
      aspectRatio = 'video',
      position = 'top',
      overlay = false,
      alt = '',
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const aspectClass = {
      square: 'aspect-square',
      video: 'aspect-video',
      portrait: 'aspect-[3/4]',
      wide: 'aspect-[21/9]',
    }[aspectRatio];

    const positionClass = {
      top: 'rounded-t-[1.3rem]',
      bottom: 'rounded-b-[1.3rem]',
      left: 'rounded-l-[1.3rem]',
      right: 'rounded-r-[1.3rem]',
    }[position];

    return (
      <div className={cn('relative overflow-hidden', aspectClass, positionClass, className)}>
        <img
          ref={ref}
          alt={alt}
          className={cn('h-full w-full object-cover', overlay && 'opacity-80')}
          data-testid={dataTestId}
          {...props}
        />
        {overlay && <div className="absolute inset-0 bg-glass-medium backdrop-blur-md" />}
      </div>
    );
  },
);
CardImage.displayName = 'CardImage';

// Card Skeleton Component for Loading States
export interface CardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  showHeader?: boolean;
  showFooter?: boolean;
  lines?: number;
  'data-testid'?: string;
}

const CardSkeleton = React.forwardRef<HTMLDivElement, CardSkeletonProps>(
  (
    {
      className,
      showHeader = true,
      showFooter = false,
      lines = 3,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        'rounded-[1.3rem] bg-glass-medium backdrop-blur-md border border-glass-medium p-6',
        className,
      )}
      data-testid={dataTestId}
      {...props}
    >
      {showHeader && (
        <div className="space-y-2 mb-4">
          <div className="h-6 w-3/4 bg-glass-heavy rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-glass-heavy rounded animate-pulse" />
        </div>
      )}

      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-4 bg-glass-heavy rounded animate-pulse',
              i === lines - 1 ? 'w-2/3' : 'w-full',
            )}
          />
        ))}
      </div>

      {showFooter && (
        <div className="flex gap-2 mt-4">
          <div className="h-10 w-24 bg-glass-heavy rounded-full animate-pulse" />
          <div className="h-10 w-24 bg-glass-heavy rounded-full animate-pulse" />
        </div>
      )}
    </div>
  ),
);
CardSkeleton.displayName = 'CardSkeleton';

// Export all components and utilities
export {
  UnifiedCard,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardImage,
  CardSkeleton,
  unifiedCardVariants,
  cardHeaderVariants,
  cardFooterVariants,
};

// Types are already exported with the interface declarations above
